/**
 * The tool's roles for display ([F-026]): the canonical order and the i18n
 * keys of their German labels and one-sentence explanations. The role ids are
 * the rights matrix's (api `MATRIX_ROLES`); which of them exist is the
 * server's answer (`/access/admin/status`), and this file only says how to
 * show them. Kept free of React so the rules are unit-testable.
 */

/** A role id of the rights matrix. Plain string: the server's list decides. */
export type AppRole = string;

/** Canonical display order of the tool-internal roles ([F-026]). */
export const ROLE_ORDER: readonly AppRole[] = [
  "melder",
  "bearbeiter",
  "incident_owner",
  "manager",
  "admin",
  "dsgvo_auditor",
];

// i18n key maps. Callers resolve these via a variable (t(labelKey)); every
// value below exists in de.ts. The German role names live in the top-level
// `roleLabels` section so other screens can reuse them.
export const ROLE_LABEL_KEY: Record<string, string> = {
  melder: "roleLabels.melder",
  bearbeiter: "roleLabels.bearbeiter",
  incident_owner: "roleLabels.incident_owner",
  manager: "roleLabels.manager",
  admin: "roleLabels.admin",
  dsgvo_auditor: "roleLabels.dsgvo_auditor",
};

/** One-sentence role explanations (Kann-Element: Rollen-Erklaertexte). */
export const ROLE_HINT_KEY: Record<string, string> = {
  melder: "adminUsers.roleHints.melder",
  bearbeiter: "adminUsers.roleHints.bearbeiter",
  incident_owner: "adminUsers.roleHints.incident_owner",
  manager: "adminUsers.roleHints.manager",
  admin: "adminUsers.roleHints.admin",
  dsgvo_auditor: "adminUsers.roleHints.dsgvo_auditor",
};

/**
 * Roles in canonical display order; a role the server knows and this file
 * does not keeps its place after the known ones, in the server's order.
 */
export function sortedRoles(held: readonly AppRole[]): AppRole[] {
  const known = ROLE_ORDER.filter((role) => held.includes(role));
  const unknown = held.filter((role) => !ROLE_ORDER.includes(role));
  return [...known, ...unknown];
}

/** The German label of a role, or the server's label, or the id itself. */
export function roleLabel(t: (key: string) => string, role: AppRole, serverLabel?: string): string {
  const key = ROLE_LABEL_KEY[role];
  return key ? t(key) : (serverLabel ?? role);
}
